import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { getCampaignById, getCampaignLinkClickData, getCampaignStats, EmailCampaign } from '../../firebase/services/campaignService';

/**
 * Component for displaying email campaign analytics
 */
const CampaignStats = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [campaign, setCampaign] = useState<EmailCampaign | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [linkStats, setLinkStats] = useState<{ url: string; totalClicks: number; uniqueClicks: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const campaignData = id ? await getCampaignById(id) : null;
        setCampaign(campaignData);
        const statsData = id ? await getCampaignStats(id) : null;
        setStats(statsData);
        const links = id ? await getCampaignLinkClickData(id) : [];
        setLinkStats(links);
      } catch (err) {
        setError('Failed to load campaign stats');
        console.error('Error loading campaign stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [id]);

  // Format date
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          sx={{ mr: 2 }}
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('../campaigns')}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Campaign Analytics: {campaign?.name || ''}
        </Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : campaign && stats ? (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" color="text.secondary">
              Subject: {campaign.subject} • Sent: {formatDate(campaign.sentAt)}
            </Typography>
          </Box>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Delivered
                  </Typography>
                  <Typography variant="h4">
                    {stats.delivered ?? 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Opens
                  </Typography>
                  <Typography variant="h4">
                    {stats.opens ?? 0} <Typography variant="subtitle2" component="span">({((stats.openRate ?? 0) * 100).toFixed(1)}%)</Typography>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Clicks
                  </Typography>
                  <Typography variant="h4">
                    {stats.clicks ?? 0} <Typography variant="subtitle2" component="span">({((stats.clickRate ?? 0) * 100).toFixed(1)}%)</Typography>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Unsubscribed
                  </Typography>
                  <Typography variant="h4">
                    {stats.unsubscribes ?? 0} <Typography variant="subtitle2" component="span">({((stats.unsubscribeRate ?? 0) * 100).toFixed(1)}%)</Typography>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Link Performance
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Link URL</TableCell>
                    <TableCell align="right">Total Clicks</TableCell>
                    <TableCell align="right">Unique Clicks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linkStats.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No link click data found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    linkStats.map((link, index) => (
                      <TableRow key={index}>
                        <TableCell>{link.url}</TableCell>
                        <TableCell align="right">{link.totalClicks}</TableCell>
                        <TableCell align="right">{link.uniqueClicks}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      ) : null}
    </Box>
  );
};

export default CampaignStats; 